<template>
  <!-- Account Wallet Desktop Page -->
  <DashboardWindow>
    <!-- START : Wallet header with title and Add new card option -->
    <template v-slot:title>
      {{ $t('wallet') }}
    </template>
    <template v-slot:title-right>
      <button
        v-if="!loading && wallet && wallet.tokenStatus === 'NORMAL' && wallet.creditPaymentMethod"
        @click="open"
        class="btn btn-link add-new-card-btn underline-link-from-center"
        ref="addModalTriggerButton"
      >
        {{ $t('addNewCard') }}
      </button>
    </template>
    <!-- END : Wallet header with title and Add new card option -->
    <template v-slot:body>
      <WCSpinner v-if="loading" size="xl" position="center" />
      <template v-if="!loading && wallet">
        <!-- Charge Accounts Section -->
        <!-- Taking this section out for now, not fully implementing this until later -->
        <!-- <DashboardWindowSection v-if="wallet.chargeAccounts">
          <h3>$t('chargeAccounts')</h3>
          <div v-if="!wallet.chargeAccounts.length">
            You do not have any charge accounts.
          </div>
          <template v-if="wallet.chargeAccounts.length > 0">
            <div
              v-for="account of wallet.chargeAccounts"
              :key="account.id"
              class="card text-center"
            >
              <div class="card-body">
                <div class="card-title">{{ account.name }}</div>
                <div class="balances">
                  <div class="balance">
                    Balance: <span class="value">{{ -account.balance | currency }}</span>
                  </div>
                  <div class="open-to-charge" v-if="account.creditLimit">
                    Open to Charge:
                    <span class="value">{{
                      (account.creditLimit - account.balance) | currency
                    }}</span>
                  </div>
                </div>
                <a
                  :href="'me/br/' + account.id"
                  class="btn btn-primary btn-add-to-charge"
                  v-if="balanceReloadEnabled"
                  >Add Funds</a
                >
              </div>
            </div>
          </template>
        </DashboardWindowSection> -->

        <!-- START : Wallet tokens Section -->
        <DashboardWindowSection v-if="wallet.tokenStatus !== 'DISABLED'">
          <h3>{{ $t('securelyTokenizedCards') }}</h3>
          <div v-if="wallet.tokenStatus === 'OFFLINE'">
            {{ $t('tokenServerUnavailable') }}
          </div>
          <div v-if="wallet.tokenStatus !== 'OFFLINE'" class="d-flex flex-wrap">
            <h5 v-if="!wallet.tokens || wallet.tokens.length === 0">
              {{ $t('noCards') }}
            </h5>
            <!-- START : Wallet card with card image, brand, expiration, default and delete options -->
            <div
              v-for="(token, index) of wallet.tokens"
              :key="`${token.id} ${token.finalDigits}`"
              class="card text-center mr-3 my-3"
            >
              <WCDesktopWalletCard
                :token="token"
                @makeDefault="defaultToken(token)"
                @deleteToken="openDeleteCardModal(token, index)"
                :faIcon="getFAIcon(token.brand)"
                :brandDisplayName="brandDisplayName(token.brand)"
                :isTokenExpired="checkTokenExpiration(token.expiration)"
                ref="walletCard"
              />
            </div>
            <!-- END : Wallet card with card image, brand, expiration, default and delete options -->
          </div>
        </DashboardWindowSection>
        <!-- END : Wallet tokens Section -->
        <div v-if="!loading && error">{{ error }}</div>
      </template>
    </template>
  </DashboardWindow>
</template>

<script>
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import ModalService from '@/modules/modals/services/modal.service';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import WCPaymentModal from '@/modules/checkout/components/WCPaymentModal/WCPaymentModal.vue';
import { MODAL_EVENTS } from '@/constants/EventConstants';
import WCConfirmationModal from '@/components/WCConfirmationModal/WCConfirmationModal.vue';
import WalletMixin from '../mixins/WalletMixin';
import WCDesktopWalletCard from './WCDesktopWalletCard/WCDesktopWalletCard.vue';

export default {
  name: 'Wallet',
  components: {
    DashboardWindowSection,
    DashboardWindow,
    WCSpinner,
    WCDesktopWalletCard,
  },
  mixins: [WalletMixin], // Mixin for fetching wallet data on created lifecycle, delete wallet, make default option for wallet and wallet details
  methods: {
    /**
     * Method to open add new card modal and display toast on success or failure
     */
    async open() {
      try {
        const response = await ModalService.open(WCPaymentModal, {
          paymentMethod: this.wallet.creditPaymentMethod,
        });
        this.handleWalletResponse(response?.data);
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'success',
            title: 'Add Wallet Card',
            message: 'Your Card has been successfully added',
          },
          timeout: 7000,
        });
      } catch (error) {
        console.error(error);
        if (error !== 'Dismissed')
          ToastService.open(WCSimpleToast, {
            props: {
              variant: 'danger',
              title: 'Error',
              message: 'Unable to save your card at this time',
            },
            timeout: 7000,
          });
      } finally {
        // Once dialog is closed move focus to the related element
        this.$refs.addModalTriggerButton.focus();
      }
    },
    /**
     * Method to open desktop modal for delete confirmation
     * @param {Object} token
     */
    async openDeleteCardModal(token, cardIndex) {
      try {
        const response = await ModalService.open(WCConfirmationModal, {
          headerIcon: 'trash',
          header: this.$t('deleteCard'),
          content: this.$t('areYouSureDelete'),
          contentName: token.finalDigits,
          confirm: this.$t('yesDelete'),
          cancel: this.$t('no'),
        });

        if (response === MODAL_EVENTS.CONFIRM) {
          this.deleteToken(token);
        }
      } catch (error) {
        console.error(error);
      } finally {
        // Once dialog is closed move focus to the related element
        if (this.$refs.walletCard[cardIndex])
          this.$refs.walletCard[cardIndex].$refs.modalTriggerButton.focus();
      }
    },
  },
};
</script>
